import i18next from 'i18next';
import { setPstTimeOffset } from '@AMIEWEB/AmWorkDesk/CoverageDesk/CoverageModal/assignHelper';
import { isNullOrUndefined } from 'app/helpers/objectHelpers';
import { ComplianceStatusProgressNotesDetails, DirectHireOption } from 'app/models/Placement/PlacementDetails';
import moment from 'moment';
import { Concatenate } from 'utils/string/string';
import { IPlacementMapAddress } from './types';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { put } from 'redux-saga/effects';

const formatAddress = address => {
  if (!address) return '';
  return address.replace(/ /g, '%20').replace(/#/g, '%23').replace(/\./g, '');
};

const formatValue = value => (value ? `%20${value.replace(/\./g, '')}` : '');

export const hasCoordinates = (latitude?: number, longitude?: number): boolean => {
  if (latitude == null || longitude == null) {
    return false;
  }
  if (latitude === 0 && longitude === 0) {
    return false;
  }
  if (latitude < -90 || latitude > 90) {
    return false;
  }
  if (longitude < -180 || longitude > 180) {
    return false;
  }
  return true;
};

export const formatPlacementGoogleMapLink = (candidate: IPlacementMapAddress, facility: IPlacementMapAddress) => {
  const domainUrl = globalThis?.app?.env?.REACT_APP_GOOGLE_DIRECTIONS_URL;

  const origin = hasCoordinates(candidate.latitude, candidate.longitude)
    ? `${candidate.latitude},${candidate.longitude}`
    : Concatenate(
        [formatAddress(candidate.address), formatValue(candidate.city), formatValue(candidate.state), formatValue(candidate.zip)],
        '%2C',
      );

  const destination = hasCoordinates(facility.latitude, facility.longitude)
    ? `${facility.latitude},${facility.longitude}`
    : Concatenate(
        [formatAddress(facility.address), formatValue(facility.city), formatValue(facility.state), formatValue(facility.zip)],
        '%2C',
      );

  return `${domainUrl}&origin=${origin}&destination=${destination}`;
};

export const getCueNoteRecipientPayload = placementId => {
  const coverageStartDate = setPstTimeOffset(
    moment(new Date()).set({
      hour: 0,
      minute: 0,
      second: 0,
    }),
  );
  const coverageEndDate = setPstTimeOffset(
    moment(new Date()).set({
      hour: 23,
      minute: 59,
      second: 59,
    }),
  );
  return {
    placementId: placementId.toString(),
    coverageStartDate: coverageStartDate,
    coverageEndDate: coverageEndDate,
  };
};

export const getLicensure = groups => {
  const licenses = [];
  if (isNullOrUndefined(groups) || groups?.length === 0) {
    return licenses;
  }
  groups.forEach(group => {
    if (isNullOrUndefined(group.skillsets) || group.skillsets.length === 0) {
      return;
    }
    group.skillsets.forEach(skillset => {
      if (
        isNullOrUndefined(skillset) ||
        isNullOrUndefined(skillset.licenses) ||
        isNullOrUndefined(skillset.licenses.orderRequirement) ||
        isNullOrUndefined(skillset.licenses.orderRequirement.data) ||
        isNullOrUndefined(skillset.licenses.orderRequirement.data.orderLicensesDetails) ||
        skillset.licenses.orderRequirement.data.orderLicensesDetails.length === 0
      ) {
        return;
      }
      skillset.licenses.orderRequirement.data.orderLicensesDetails.forEach(orderLicensesDetail => {
        if (orderLicensesDetail.licenseAbbreviation && orderLicensesDetail.stateAbbreviation) {
          licenses.push(`${orderLicensesDetail.licenseAbbreviation} - ${orderLicensesDetail.stateAbbreviation}`);
        } else if (orderLicensesDetail.licenseAbbreviation && orderLicensesDetail.stateAbbreviation === null) {
          licenses.push(`${orderLicensesDetail.licenseAbbreviation}`);
        } else if (orderLicensesDetail.licenseAbbreviation === null && orderLicensesDetail.stateAbbreviation) {
          licenses.push(`${orderLicensesDetail.stateAbbreviation}`);
        }
      });
    });
  });
  return licenses;
};

export const getCertificates = groups => {
  const certificates = [];
  if (isNullOrUndefined(groups) || groups?.length === 0) {
    return certificates;
  }
  groups.forEach(group => {
    if (isNullOrUndefined(group.skillsets) || group.skillsets.length === 0) {
      return;
    }
    group.skillsets.forEach(skillset => {
      if (
        isNullOrUndefined(skillset) ||
        isNullOrUndefined(skillset.certifications) ||
        isNullOrUndefined(skillset.certifications.orderRequirement) ||
        isNullOrUndefined(skillset.certifications.orderRequirement.data) ||
        isNullOrUndefined(skillset.certifications.orderRequirement.data.certifications) ||
        skillset.certifications.orderRequirement.data.certifications.length === 0
      ) {
        return;
      }
      skillset.certifications.orderRequirement.data.certifications.forEach(certification => {
        if (certification.abbreviation && certification.issuedBy) {
          certificates.push(`${certification.abbreviation} - ${certification.issuedBy}`);
        } else if (certification.abbreviation && certification.issuedBy === null) {
          certificates.push(`${certification.abbreviation}`);
        } else if (certification.abbreviation === null && certification.issuedBy) {
          certificates.push(`${certification.issuedBy}`);
        }
      });
    });
  });
  return certificates;
};

export const searchNoteListByText = (
  searchText: string,
  notesList: Array<ComplianceStatusProgressNotesDetails>,
): Array<ComplianceStatusProgressNotesDetails> => {
  if (searchText === '') {
    return notesList;
  }
  let filteredNoteList = notesList.filter(
    note =>
      note.notes.toLowerCase().includes(searchText.toLowerCase()) ||
      note.userName.toLowerCase().includes(searchText.toLowerCase()),
  );
  return filteredNoteList;
};

export function* handleResponse(response) {
  for (const result of response.data) {
    yield put(
      globalActions.setStackSnackBar({
        message: result.message,
        severity: result.severity,
      }),
    );
  }
}

export const SSN_ERROR_ID_DUPLICATE = 60074;

export const getSsnErrorDialogPayload = (response: { status?: number; data?: any }) => {
  const t = (key: string) => (typeof i18next?.t === 'function' ? i18next.t(key) : key);
  const status = response?.status ?? 500;
  const data = response?.data ?? {};
  const ssnData = data?.ssn ?? data;
  const errorId = ssnData?.errorId ?? data?.errorId;
  const errorMessage = ssnData?.errorMessage ?? data?.errorMessage ?? '';

  if (status === 400 && errorId === SSN_ERROR_ID_DUPLICATE) {
    return { title: t('candidate.ssnUpdate.duplicateSsnTitle'), message: errorMessage || t('candidate.ssnUpdate.errorOccurredMessage') };
  }
  if (status === 403) {
    return { title: t('candidate.ssnUpdate.errorOccurredTitle'), message: errorMessage || t('candidate.ssnUpdate.unauthorizedMessage') };
  }
  if (status === 409) {
    return { title: t('candidate.ssnUpdate.errorOccurredTitle'), message: t('candidate.ssnUpdate.conflictMessage') };
  }
  return { title: t('candidate.ssnUpdate.errorOccurredTitle'), message: t('candidate.ssnUpdate.errorOccurredMessage') };
};

export const getDirectHireSelectedOption = (
  directHireOptions: DirectHireOption[],
  id: string | null,
  directHireTypeDescription?: string | null,
) => {
  const selectedOption: DirectHireOption = { id: id ?? '', description: directHireTypeDescription ?? '' };
  if (id === null) {
    return selectedOption;
  }
  const optionIndex = directHireOptions?.findIndex(directHireOption => directHireOption.id === id);
  if (optionIndex > -1) {
    return directHireOptions[optionIndex];
  } else {
    return selectedOption;
  }
};
